import { type StdioOptions, spawn } from 'node:child_process'
import path from 'node:path'

const PG_SSL_WARNING_START =
  "Warning: SECURITY WARNING: The SSL modes 'prefer', 'require', and 'verify-ca' are treated as aliases for 'verify-full'."
const PG_SSL_WARNING_END =
  'See https://www.postgresql.org/docs/current/libpq-ssl.html for libpq SSL mode definitions.'
const NODE_TRACE_WARNING_HINT = '(Use `node --trace-warnings'
const DRIZZLE_NOISE_PATTERNS = [
  /^No config path provided, using default 'drizzle\.config\.ts'$/,
  /^Reading config file '.+drizzle\.config\.ts'$/,
  /^Using 'pg' driver for database querying$/,
  /^\[.\] Pulling schema from database\.\.\.$/u,
  /^\[.\] Changes applied$/u
]
// A buffered partial line is held back only while it could still become one of the
// suppressed lines above; anything else (e.g. an interactive prompt, which has no
// trailing newline) is flushed immediately so it stays visible.
const SUPPRESSIBLE_LINE_STARTS = [
  'No config path provided',
  "Reading config file '",
  "Using 'pg' driver",
  '(node:',
  '['
]
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching the ANSI escape character is the point
const ANSI_ESCAPE_PATTERN = /\u001B\[[0-9;]*[A-Za-z]/g

interface NoiseFilterState {
  suppressingPgSslWarning: boolean
  suppressNextTraceHint: boolean
}

interface DrizzlePushOptions {
  interactive?: boolean
  /**
   * Interactive mode only: called once, right before the first visible chunk
   * (after noise filtering) reaches the terminal — e.g. so a caller can clear
   * a spinner before Drizzle's rename prompts render. Never called when the
   * push produces no visible output.
   */
  onOutput?: () => void
}

export interface DrizzlePushResult {
  success: boolean
  error: string | null
}

/** Interactive runs keep stdin on the terminal so Drizzle rename prompts stay answerable. */
export function resolveDrizzlePushStdio(interactive: boolean | undefined): StdioOptions {
  return interactive ? ['inherit', 'pipe', 'pipe'] : 'pipe'
}

function stripAnsi(text: string): string {
  return text.replace(ANSI_ESCAPE_PATTERN, '').replaceAll('\r', '')
}

function shouldSuppressOutputLine(line: string, state: NoiseFilterState): boolean {
  const text = stripAnsi(line).trim()

  if (state.suppressNextTraceHint) {
    state.suppressNextTraceHint = false
    if (text.startsWith(NODE_TRACE_WARNING_HINT)) {
      return true
    }
  }

  if (text.includes(PG_SSL_WARNING_START)) {
    state.suppressingPgSslWarning = true
    return true
  }

  if (state.suppressingPgSslWarning) {
    if (text.includes(PG_SSL_WARNING_END)) {
      state.suppressingPgSslWarning = false
      state.suppressNextTraceHint = true
    }
    return true
  }

  return DRIZZLE_NOISE_PATTERNS.some((pattern) => pattern.test(text))
}

function filterNoiseLines(output: string, state: NoiseFilterState): string {
  const lines = output.match(/[^\n]*\n|[^\n]+/g) ?? []
  return lines.filter((line) => !shouldSuppressOutputLine(line, state)).join('')
}

export function filterDrizzleNoise(output: string): string {
  return filterNoiseLines(output, {
    suppressingPgSslWarning: false,
    suppressNextTraceHint: false
  })
}

function couldBecomeSuppressible(partial: string, state: NoiseFilterState): boolean {
  if (state.suppressingPgSslWarning || state.suppressNextTraceHint) {
    return true
  }

  const text = stripAnsi(partial)
  return SUPPRESSIBLE_LINE_STARTS.some((start) => text.startsWith(start) || start.startsWith(text))
}

function createDrizzleNoiseFilter(forward: (chunk: string) => void): {
  write: (chunk: string) => void
  end: () => void
} {
  const state: NoiseFilterState = {
    suppressingPgSslWarning: false,
    suppressNextTraceHint: false
  }
  let buffer = ''

  return {
    write(chunk: string) {
      buffer += chunk
      const lastNewlineIndex = buffer.lastIndexOf('\n')
      if (lastNewlineIndex !== -1) {
        const complete = buffer.slice(0, lastNewlineIndex + 1)
        buffer = buffer.slice(lastNewlineIndex + 1)
        const filtered = filterNoiseLines(complete, state)
        if (filtered) {
          forward(filtered)
        }
      }

      if (buffer && !couldBecomeSuppressible(buffer, state)) {
        forward(buffer)
        buffer = ''
      }
    },
    end() {
      if (!buffer) {
        return
      }

      const filtered = filterNoiseLines(buffer, state)
      buffer = ''
      if (filtered) {
        forward(filtered)
      }
    }
  }
}

/** Run drizzle-kit push, suppressing config banners, progress spinners, and the pg sslmode advisory. */
export function runDrizzlePush(
  cwd: string,
  options: DrizzlePushOptions = {}
): Promise<DrizzlePushResult> {
  return new Promise((resolve) => {
    const drizzleBin = path.join(cwd, 'node_modules', '.bin', 'drizzle-kit')
    const child = spawn(drizzleBin, ['push', '--force'], {
      cwd,
      stdio: resolveDrizzlePushStdio(options.interactive),
      env: { ...process.env }
    })
    let stdout = ''
    let stderr = ''
    let outputNotified = false
    const notifyOutput = () => {
      if (outputNotified) return
      outputNotified = true
      options.onOutput?.()
    }
    const stderrFilter = createDrizzleNoiseFilter((chunk) => {
      if (options.interactive) {
        notifyOutput()
        process.stderr.write(chunk)
        return
      }

      stderr += chunk
    })
    const stdoutFilter = createDrizzleNoiseFilter((chunk) => {
      if (options.interactive) {
        notifyOutput()
        process.stdout.write(chunk)
        return
      }

      stdout += chunk
    })

    child.stdout?.on('data', (chunk: Buffer) => {
      stdoutFilter.write(chunk.toString())
    })
    child.stderr?.on('data', (chunk: Buffer) => {
      stderrFilter.write(chunk.toString())
    })
    child.on('close', (code, signal) => {
      stdoutFilter.end()
      stderrFilter.end()

      if (options.interactive) {
        if (code === 0) {
          resolve({ success: true, error: null })
          return
        }

        resolve({
          success: false,
          error: signal
            ? `drizzle-kit push exited with signal ${signal}`
            : `drizzle-kit push exited with code ${code}`
        })
        return
      }

      const output = `${stdout}\n${stderr}`.trim()
      const reportedError = output.match(/(?:^|\n)Error: [\s\S]*$/)?.[0]?.trimStart()

      if (code === 0 && !reportedError) {
        resolve({ success: true, error: null })
        return
      }

      resolve({
        success: false,
        error: reportedError ?? (stderr || stdout || `drizzle-kit push exited with code ${code}`)
      })
    })
    child.on('error', (err) => {
      resolve({ success: false, error: err.message })
    })
  })
}
